import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Chip, Divider, Switch, Typography } from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { SHORTCUT_GROUPS } from '../keyboardShortcuts';
import { KeyCombo } from './KeyCap';

const GROUP_ICONS = {
  'Add relatives': GroupAddIcon,
  Panels: ViewSidebarIcon,
  'Edit & select': TouchAppIcon,
  View: ZoomInIcon
};

export default function ShortcutsHelp({ open, onClose, enabled, onToggle }) {
  const theme = useTheme();

  return (
    <CustomDialog
      open={open}
      handleClose={onClose}
      title={'Keyboard shortcuts'}
      allowBackgroundClose={true}
      maxWidth={'md'}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          p: 1.5,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.default
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Switch checked={!!enabled} onChange={onToggle} color="secondary" />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
              {enabled ? 'Keyboard shortcuts on' : 'Keyboard shortcuts off'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              {enabled
                ? 'Turn off to disable everything below'
                : 'Turn on to enable the keys below'}
            </Typography>
          </Box>
        </Box>
        <Chip
          size="small"
          label={enabled ? 'Active' : 'Disabled'}
          color={enabled ? 'success' : 'default'}
          variant={enabled ? 'filled' : 'outlined'}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          opacity: enabled ? 1 : 0.45,
          pointerEvents: enabled ? 'auto' : 'none',
          transition: 'opacity 0.15s ease'
        }}
      >
        {SHORTCUT_GROUPS.map((group) => {
          const GroupIcon = GROUP_ICONS[group.title];
          return (
            <Box
              key={group.title}
              sx={{
                p: 1.75,
                borderRadius: '10px',
                border: '1px solid',
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.background.paper
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mb: group.hint ? 0.25 : 1
                }}
              >
                {GroupIcon && (
                  <GroupIcon
                    fontSize="small"
                    sx={{ color: theme.palette.secondary.main }}
                  />
                )}
                <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                  {group.title}
                </Typography>
              </Box>
              {group.hint && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: theme.palette.text.secondary,
                    mb: 1
                  }}
                >
                  {group.hint}
                </Typography>
              )}
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {group.items.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2
                    }}
                  >
                    <Typography sx={{ fontSize: 13 }}>{item.label}</Typography>
                    <KeyCombo keys={item.keys} />
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 2, color: theme.palette.text.disabled }}
      >
        Shortcuts are inactive while typing in a text field. Undo/redo and
        copy/paste are not yet available.
      </Typography>
    </CustomDialog>
  );
}
